import { ArrowBack } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import FlexContainer from 'components/FlexContainer';
import { ClaimCard } from 'features/fact-grabbing/ClaimCard';
import { ClaimCardReferencesLoader } from 'features/fact-grabbing/ClaimCardReferencesLoader';
import { ClaimReferenceList } from 'features/fact-grabbing/ClaimReferenceList';
import { JudgmentProgressBars } from 'features/fact-grabbing/JudgmentProgressBars';
import { ScrollToTopButton } from 'features/fact-grabbing/ScrollToTopButton';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { LoadingAnimationWithAlternatingMessages } from 'features/loading-spinner/LoadingAnimationWithAlternatingMessages';
import { useRef } from 'react';
import { ApiClaim } from 'services/backofficeIntegration/http/endpoints/fact-grabbing/httpGetClaimsByText';
import styled from 'styled-components';

type Props = {
  claim: ApiClaim;
  onBackClick: () => void;
};

export const ClaimDetailView = ({ claim, onBackClick }: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <Root>
      <ScrollingContainer ref={rootRef} direction="column" gap="two">
        <div>
          <Button startIcon={<ArrowBack />} onClick={onBackClick} variant="text" color="primary">
            <FormattedMessage id="aiWriter.inspirations.factGrabbing.claim.details.back_to_overview.label" />
          </Button>
        </div>

        <ClaimContainer>
          <ClaimCard claim={claim} showReviewButton={false} isClickable={false} />
        </ClaimContainer>

        <ClaimCardReferencesLoader claim={claim}>
          {({ judgmentCounts, claimReferences, isLoading }) => (
            <>
              {isLoading ? (
                <LoadingContainer>
                  <LoadingAnimationWithAlternatingMessages />
                </LoadingContainer>
              ) : (
                <ContentContainer>
                  <Typography variant="subtitle1" fontWeight="bold">
                    <FormattedMessage
                      id="aiWriter.inspirations.factGrabbing.claim.details.sources.label"
                      values={{
                        count: claimReferences.length
                      }}
                    />
                  </Typography>

                  <JudgmentProgressBars judgmentCounts={judgmentCounts} />

                  <ClaimReferenceList references={claimReferences} />
                </ContentContainer>
              )}
            </>
          )}
        </ClaimCardReferencesLoader>

        <ScrollToTopButton scrollContainerRef={rootRef} threshold={50} />
      </ScrollingContainer>
    </Root>
  );
};

const Root = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'five'
})`
  width: 100%;
`;

const ScrollingContainer = styled(FlexContainer)`
  position: relative;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  // Gave some spacings to the right to avoid border or box-shadow touching the scrollbar
  padding-right: ${({ theme }) => theme.spacings.one};
`;

const ClaimContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.componentsInputfieldOutlinedEnabledborder};
  border-radius: ${({ theme }) => theme.borderRadius.one};
  margin-bottom: ${({ theme }) => theme.spacings.two};
  margin-left: ${({ theme }) => theme.spacings.three};
`;

const LoadingContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacings.five};
`;

const ContentContainer = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'two'
})`
  margin-left: ${({ theme }) => theme.spacings.three};
`;
